import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { User } from '../classes/user';
import {
  Account,
  AccountPatchRequest,
  AccountPatchResponse,
  AccountResponse,
} from '../models/account';
import { environment } from 'src/environments/environment';
import _ from 'lodash';
import { map, switchMap, first, tap, finalize } from 'rxjs/operators';
import { arraysToObjects } from '../array-fix/functions/array-to-object';
import { objectsToArrays } from '../array-fix/functions/object-to-array';
import { SiteService } from './site.service';
import {
  captureWireSnapshot,
  diffWire,
  FlatWireData,
  isEmptySparseDiff,
  stripPhantomNullCreates,
} from '../classes/sparse-save';
import { of } from 'rxjs';

/**
 * Marketing content subtrees (mirrors Account.CONTENT_SUBTREES server-side).
 * The server excludes them from default account GETs and PATCH responses and
 * write-protects them on the generic PATCH; the CMS pages opt in via
 * ensureFullAccount(). Once loaded they're preserved across emits client-side.
 */
// 'content' covers every content.<kind> collection (regions, suburbs,
// charities, blogs, faqs); only funeralCosts predates the namespace at a
// top-level path.
const CONTENT_SUBTREES = ['content', 'funeralCosts'];

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  account$!: Observable<Account>;

  private accountSource!: ReplaySubject<Account>;

  // Latest emitted account — ReplaySubject has no synchronous accessor.
  private lastAccount: Account | null = null;

  // #148-style wire snapshot of account.data at the last emit; the sparse
  // SHADOW diff in patch() runs against this. Null until the first emit.
  private baselineWire: FlatWireData | null = null;

  constructor(
    private siteService: SiteService,
    private http: HttpClient
  ) {
    this.accountSource = new ReplaySubject<Account>(1);
    this.account$ = this.accountSource.asObservable();
  }

  /**
   * Single emit point: preserves already-loaded content subtrees (server
   * responses omit them), tracks the latest account, and refreshes the sparse
   * baseline snapshot.
   */
  private emitAccount(account: Account): void {
    const previousData: any = this.lastAccount && (this.lastAccount as any).data;
    const nextData: any = (account as any).data;

    if (previousData && nextData) {
      for (const subtree of CONTENT_SUBTREES) {
        if (nextData[subtree] === undefined && previousData[subtree] !== undefined) {
          nextData[subtree] = previousData[subtree];
        }
      }
    }

    this.lastAccount = account;

    try {
      this.baselineWire = captureWireSnapshot(arraysToObjects(nextData));
    } catch (e) {
      // A failed snapshot only disables the shadow diff — never the save.
      this.baselineWire = null;
    }

    this.accountSource.next(account);
  }

  get(full: boolean = false): Observable<AccountResponse> {
    const url =
      environment.api.host + environment.api.paths.api.account.get + (full ? '?full=1' : '');

    this.siteService.addSubscriptionLog(
      this,
      'account.service.ts->get->this.http.get<AccountResponse>(url)'
    );

    return this.http.get<AccountResponse>(url).pipe(
      finalize(() =>
        this.siteService.setSubscriptionLogFinalised(
          'account.service.ts->get->this.http.get<AccountResponse>(url)'
        )
      ),
      map((res) => {
        res.data.data = objectsToArrays(res.data.data);

        this.emitAccount(res.data);

        return res;
      })
    );
  }

  /**
   * The CMS pages read content/funeralCosts off account$; the default account fetch
   * no longer carries them. Completes immediately when they're already loaded,
   * otherwise re-fetches the full account (which emits with content preserved
   * from then on).
   */
  ensureFullAccount(): Observable<boolean> {
    const data: any = this.lastAccount && (this.lastAccount as any).data;
    const hasContent = !!data && CONTENT_SUBTREES.every((s) => data[s] !== undefined);

    if (hasContent) {
      return of(true);
    }

    return this.get(true).pipe(map(() => true));
  }

  post(data: any): Observable<any> {
    const url = environment.api.host + environment.api.paths.api.account.post;

    this.siteService.addSubscriptionLog(
      this,
      'account.service.ts->post->this.http.post<AccountResponse>(url, data)'
    );

    return this.http.post<AccountResponse>(url, data).pipe(
      finalize(() =>
        this.siteService.setSubscriptionLogFinalised(
          'account.service.ts->post->this.http.post<AccountResponse>(url, data)'
        )
      ),
      map((res) => {
        this.emitAccount(res.data);
        return res;
      })
    );
  }

  patch(data: AccountPatchRequest): Observable<AccountPatchResponse> {
    const url = environment.api.host + environment.api.paths.api.account.patch;

    let arrayToObject: any = arraysToObjects(data);

    // #148 sparse protocol for account saves. 'on': the body carries ONLY the
    // changed paths ({ __sparse: { changed, deleted } }), so two users editing
    // different records of the same collection touch disjoint paths and can't
    // clobber each other (same record = last write wins by design). 'shadow':
    // full payload + logged dry-run diff. Any failure (no baseline yet, diff
    // error) falls back to the legacy full save — never block the save.
    // Content subtrees are excluded either way: they're write-protected
    // server-side and owned by the kind-scoped content route.
    const sparseMode = environment.accountSparseSaves;

    if (sparseMode !== 'off' && this.baselineWire) {
      try {
        const currentWire = captureWireSnapshot(arrayToObject);
        const diff = stripPhantomNullCreates(
          diffWire(this.baselineWire, currentWire),
          this.baselineWire
        );

        const isContentPath = (path: string) =>
          CONTENT_SUBTREES.some((s) => path === s || path.startsWith(`${s}.`));

        for (const path of Object.keys(diff.changed)) {
          if (isContentPath(path)) {
            delete diff.changed[path];
          }
        }
        diff.deleted = diff.deleted.filter((path) => !isContentPath(path));

        if (sparseMode === 'on') {
          arrayToObject = { __sparse: diff };
        } else if (!isEmptySparseDiff(diff)) {
          arrayToObject.__sparseShadow = diff;
        }
      } catch (e) {
        // Fall back to the full payload — never block the save.
      }
    }

    this.siteService.addSubscriptionLog(this, 'account.service.ts->patch->this.account$');

    return this.account$.pipe(
      finalize(() =>
        this.siteService.setSubscriptionLogFinalised('account.service.ts->patch->this.account$')
      ),
      first(),
      switchMap((account) => {
        return this.http.patch<AccountPatchResponse>(url, {
          data: arrayToObject,
          requestedAt: account.requestedAt,
        });
      }),
      map((res) => {
        const data = objectsToArrays(res.data.account);

        this.emitAccount(data);

        return res;
      })
    );
  }

  /**
   * Content module write (regions / suburbs) — hits the RBAC-gated
   * PATCH /account/content (Content Manager / Operation / Developer), NOT the
   * open PATCH /account, so the Content-Manager role is enforced server-side.
   *
   * Client array items carry _meta.{id,index,deleted}; the server route wants
   * { id, ...fields } and rebuilds _meta itself — so strip _meta and drop
   * soft-deleted items (the route replaces the whole collection). Re-fetches via
   * get() afterwards so the account stream stays correct without depending on
   * this route's response envelope.
   */
  patchContent(
    kind: 'regions' | 'suburbs' | 'charities' | 'blogs',
    items: any[]
  ): Observable<AccountResponse> {
    const url = environment.api.host + environment.api.paths.api.account.patch + '/content';

    const payload = (items || [])
      .filter((it) => it && !it._meta?.deleted)
      .map((it) => {
        const { _meta, ...fields } = it;
        return { id: _meta?.id, ...fields };
      });

    this.siteService.addSubscriptionLog(this, 'account.service.ts->patchContent->this.http.patch');

    return this.http.patch(url, { kind, items: payload }).pipe(
      finalize(() =>
        this.siteService.setSubscriptionLogFinalised('account.service.ts->patchContent->this.http.patch')
      ),
      // Full refresh: the default GET is dieted, and the CMS pages need the
      // server's post-write content state (not the locally-mutated copy).
      switchMap(() => this.get(true))
    );
  }

  /**
   * Per-item content write — the concurrency-safe path the CMS editors use.
   * Sends ONLY the edited item (upsert) so two content managers editing
   * different records of the same kind can't clobber each other; the legacy
   * patchContent whole-replace remains for the seed scripts.
   */
  patchContentItem(
    kind: 'regions' | 'suburbs' | 'charities' | 'blogs' | 'funeralCosts' | 'faqs' | 'faqCategories' | 'pages' | 'menus' | 'templateCopy' | 'siteServices' | 'pageTemplates' | 'globalBlocks',
    item: any
  ): Observable<AccountResponse> {
    const url = environment.api.host + environment.api.paths.api.account.patch + '/content';

    const { _meta, ...fields } = item || {};
    const payload: any = { id: _meta?.id, ...fields };
    // Soft-delete flag rides the upsert (funeralCosts): the server preserves it as
    // _meta.deleted so the item stays restorable instead of being dropped.
    if (_meta?.deleted === true) {
      payload.deleted = true;
    }

    this.siteService.addSubscriptionLog(this, 'account.service.ts->patchContentItem->this.http.patch');

    return this.http.patch(url, { kind, upsert: [payload] }).pipe(
      finalize(() =>
        this.siteService.setSubscriptionLogFinalised(
          'account.service.ts->patchContentItem->this.http.patch'
        )
      ),
      switchMap(() => this.get(true))
    );
  }

  /**
   * Several content writes in ONE request (#225).
   *
   * The server already applies `upsert[]` and `remove[]` per item, each to its
   * own `<base>.<id>` subtree — the single-item methods below were sending one
   * request each and refetching the whole account every time, so saving 12 FAQ
   * categories cost 24 round trips and got linearly slower as categories were
   * added.
   *
   * This cannot be fixed by running those in parallel: each one resolves with
   * a full account fetch and the last response wins, so overlapping them can
   * resurrect a stale subtree. Batching removes the sequence instead of racing
   * it — one write, one refetch.
   */
  patchContentItems(
    kind: 'regions' | 'suburbs' | 'charities' | 'blogs' | 'funeralCosts' | 'faqs' | 'faqCategories' | 'pages' | 'menus' | 'templateCopy' | 'siteServices',
    items: any[],
    remove: string[] = []
  ): Observable<AccountResponse> {
    const url = environment.api.host + environment.api.paths.api.account.patch + '/content';

    const upsert = (items || []).map((item) => {
      const { _meta, ...fields } = item || {};
      const payload: any = { id: _meta?.id, ...fields };
      if (_meta?.deleted === true) {
        payload.deleted = true;
      }
      return payload;
    });

    this.siteService.addSubscriptionLog(this, 'account.service.ts->patchContentItems->this.http.patch');

    return this.http.patch(url, { kind, upsert, remove }).pipe(
      finalize(() =>
        this.siteService.setSubscriptionLogFinalised(
          'account.service.ts->patchContentItems->this.http.patch'
        )
      ),
      switchMap(() => this.get(true))
    );
  }

  /** Per-item content removal — deletes only the named id (see patchContentItem). */
  removeContentItem(
    kind: 'regions' | 'suburbs' | 'charities' | 'blogs' | 'funeralCosts' | 'faqs' | 'faqCategories' | 'pages' | 'menus' | 'templateCopy' | 'siteServices' | 'globalBlocks',
    id: string
  ): Observable<AccountResponse> {
    const url = environment.api.host + environment.api.paths.api.account.patch + '/content';

    this.siteService.addSubscriptionLog(this, 'account.service.ts->removeContentItem->this.http.patch');

    return this.http.patch(url, { kind, remove: [id] }).pipe(
      finalize(() =>
        this.siteService.setSubscriptionLogFinalised(
          'account.service.ts->removeContentItem->this.http.patch'
        )
      ),
      switchMap(() => this.get(true))
    );
  }
}
